import { AlertCircle, ArrowLeft, ArrowRight, Bell, CheckCircle2, XCircle } from 'lucide-react';
import { QueueDeleteDialog } from '@/components/common/QueueDeleteDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn, formatRelativeTime, formatWorkflowStateLabel } from '@/lib/utils';

export interface ExceptionStickyHeaderProps {
  state: string;
  lastTransitionedBy: string | null;
  lastTransitionedAt: string | null;
  feedbackPresent: boolean;
  isDuplicateReview: boolean;
  isDraftRejectedReview: boolean;
  /** Row is in a state where Save can force-advance even with no pending edits. */
  canForceAdvance: boolean;
  hasResolutionChanges: boolean;
  saveResolutionPending: boolean;
  /**
   * ADO #700: labels for key fields the operator modified but did not yet
   * justify. When non-empty, Save is disabled and the tooltip lists them.
   */
  missingReasonLabels: readonly string[];
  duplicateDecisionPending: boolean;
  canDelete: boolean;
  deleteQueueId: string | undefined;
  onBack: () => void;
  onSave: () => void;
  onResolveDuplicate: () => void;
  onForfeit: () => void;
  onOpenFeedback: () => void;
  onDeleted: () => void;
}

export function ExceptionStickyHeader(props: ExceptionStickyHeaderProps): JSX.Element {
  // Single Save button. The backend treats every save from an Exception -
  // Awaiting Operator gate as a force-advance through validation, so a click
  // with no pending edits (the "approve current matching" intent) and a click
  // with edits (the "save changes and move on" intent) both push the row
  // forward. Disabled when the mutation is in flight or when neither path is
  // meaningful (no edits AND nothing to force-advance).
  // ADO #700: Save is also blocked when any modified key field is missing
  // an operator-supplied reason. The exact tooltip text is mandated by the
  // spec; do not localise without updating the spec.
  const missingReasons = props.missingReasonLabels;
  const hasMissingReasons = missingReasons.length > 0;
  const saveDisabled =
    props.saveResolutionPending ||
    hasMissingReasons ||
    (!props.hasResolutionChanges && !props.canForceAdvance);
  const missingReasonsTooltip = hasMissingReasons
    ? `Missing feedback for fields '${missingReasons.join(', ')}'`
    : null;
  return (
    <div className="sticky top-0 z-30 -mx-6 flex items-center justify-between border-b bg-background/95 px-6 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <Button variant="ghost" size="sm" onClick={props.onBack}>
        <ArrowLeft className="h-4 w-4" />
        Back to queue
      </Button>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {missingReasonsTooltip ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrap the disabled Button in a span so Radix can pick up
                    hover events even while pointer-events are blocked. */}
                <span tabIndex={0}>
                  <Button
                    type="button"
                    size="sm"
                    onClick={props.onSave}
                    disabled={saveDisabled}
                    aria-disabled={saveDisabled}
                  >
                    <AlertCircle className="h-4 w-4" />
                    Save
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{missingReasonsTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button type="button" size="sm" onClick={props.onSave} disabled={saveDisabled}>
            <CheckCircle2 className="h-4 w-4" />
            Save
          </Button>
        )}
        {props.isDuplicateReview ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={props.onResolveDuplicate}
            disabled={props.duplicateDecisionPending}
          >
            <ArrowRight className="h-4 w-4" />
            Resolve duplicate
          </Button>
        ) : null}
        {props.isDraftRejectedReview ? (
          <Button type="button" size="sm" variant="destructive" onClick={props.onForfeit}>
            <XCircle className="h-4 w-4" />
            Forfeit
          </Button>
        ) : null}
        <span>
          {props.lastTransitionedBy ?? 'system'} -{' '}
          {props.lastTransitionedAt ? formatRelativeTime(props.lastTransitionedAt) : 'now'}
        </span>
        <Badge variant="warning">{formatWorkflowStateLabel(props.state)}</Badge>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="relative h-9 w-9"
          onClick={props.onOpenFeedback}
          aria-label="Open feedback note"
        >
          <Bell className="h-4 w-4" />
          <span
            className={cn(
              'absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-muted transition-opacity',
              !props.feedbackPresent && 'bg-warning opacity-100',
              props.feedbackPresent && 'opacity-40',
            )}
          />
        </Button>
        {props.canDelete && props.deleteQueueId ? (
          <QueueDeleteDialog queueId={props.deleteQueueId} onDeleted={props.onDeleted} />
        ) : null}
      </div>
    </div>
  );
}
